import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  PageTheme,
  palettes,
  shapes,
  UnifiedTheme,
} from '@backstage/theme';

// ---------------------------------------------------------------------------
// Custom portal themes — SINGLE SOURCE OF TRUTH.
//
// Everything customizable lives in `themeConfig` below. One factory
// (`buildTheme`) turns that config into both the light and the dark variant,
// so there is exactly one definition to maintain.
//
// Type roles (self-hosted variable fonts, see assets/fonts/fonts.css):
//   Archivo         -> display: page titles, card titles
//   Instrument Sans -> UI: body, buttons, navigation
//   JetBrains Mono  -> data: numbers, model ids, table headers, eyebrows
//
// The `light` palette is derived from the TotalEnergies Design System (TDS);
// the `dark` variant ("TDS midnight") reuses the same roles on blue-night
// surfaces with a single accent (#6689F5, contrast-checked on #121829).
// ---------------------------------------------------------------------------

export const FONT_UI =
  "'Instrument Sans', system-ui, -apple-system, 'Segoe UI', sans-serif";
export const FONT_DISPLAY = `'Archivo', ${FONT_UI}`;
export const FONT_DATA =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

// TotalEnergies spectrum hairline (logo hues; align with official TDS tokens
// when available). Used as the header's bottom border in both variants, and
// by the home hero.
export const TE_SPECTRUM =
  'linear-gradient(90deg, #ED0000, #FF7D00, #FFC700, #4DA32F, #00A3E0, #3360F6)';

type ThemeColors = {
  primary: string;
  secondary: string;
  error: string;
  warning: string;
  info: string;
  success: string;
  backgroundDefault: string;
  backgroundPaper: string;
  // Foreground pair for text on paper surfaces (header title / subtitle).
  ink: string;
  inkSecondary: string;
  divider: string;
  navigationBackground: string;
  navigationIndicator: string;
  navigationColor: string;
  navigationSelectedColor: string;
  // Gradient colors for item-card banners (template tiles, etc.).
  cardColors: [string, string];
};

const themeConfig: Record<'light' | 'dark', ThemeColors> = {
  // TotalEnergies Design System — light.
  light: {
    primary: '#3360F6',
    secondary: '#525863',
    error: '#B40000',
    warning: '#645802',
    info: '#1F49D2',
    success: '#366300',
    backgroundDefault: '#F7F8FA',
    backgroundPaper: '#FFFFFF',
    ink: '#222429',
    inkSecondary: '#5A6270',
    divider: '#E4E7EE',
    navigationBackground: '#16181D',
    navigationIndicator: '#3360F6',
    navigationColor: '#C9CCD6',
    navigationSelectedColor: '#FFFFFF',
    cardColors: ['#3E6DF5', '#2A54DE'],
  },
  // "TDS midnight" — same roles, blue-night surfaces, one accent.
  dark: {
    primary: '#6689F5',
    secondary: '#9AA4BC',
    error: '#F07178',
    warning: '#E3B341',
    info: '#6689F5',
    success: '#7FBF4D',
    backgroundDefault: '#0C111C',
    backgroundPaper: '#121829',
    ink: '#E9ECF5',
    inkSecondary: '#9AA4BC',
    divider: '#26304A',
    navigationBackground: '#080C14',
    navigationIndicator: '#6689F5',
    navigationColor: '#9AA4BC',
    navigationSelectedColor: '#E9ECF5',
    cardColors: ['#24304F', '#1B2340'],
  },
};

type Variant = 'light' | 'dark';

// Brand gradient, reserved for "create" affordances (scaffolder tiles via
// BackstageItemCardHeader, home Self Services card).
export const brandGradient = (mode: Variant): string => {
  const [from, to] = themeConfig[mode].cardColors;
  return `linear-gradient(120deg, ${from}, ${to})`;
};

// Backstage requires the full typography block. Headings use the display
// face; sizes follow the portal scale (entity name 26px = h3-equivalent).
const typography = {
  htmlFontSize: 16,
  fontFamily: FONT_UI,
  h1: {
    fontSize: 40,
    fontWeight: 750,
    marginBottom: 10,
    fontFamily: FONT_DISPLAY,
  },
  h2: {
    fontSize: 32,
    fontWeight: 750,
    marginBottom: 8,
    fontFamily: FONT_DISPLAY,
  },
  h3: {
    fontSize: 26,
    fontWeight: 750,
    marginBottom: 6,
    fontFamily: FONT_DISPLAY,
  },
  h4: {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 6,
    fontFamily: FONT_DISPLAY,
  },
  h5: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 4,
    fontFamily: FONT_DISPLAY,
  },
  h6: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 2,
    fontFamily: FONT_DISPLAY,
  },
};

// Flat, paper-colored page banner: the brand moves from the decorative wave
// gradient to the 3px spectrum hairline under the header.
const flatPageTheme = (bg: string, fontColor: string): PageTheme => ({
  colors: [bg],
  shape: 'none',
  backgroundImage: `linear-gradient(90deg, ${bg}, ${bg})`,
  fontColor,
});

// Shared component overrides, parameterized by variant colors. Targets public
// named components only (BackstageHeader, MuiTab, ...) — the supported
// theming channel, resilient across Backstage upgrades.
const buildComponents = (cfg: ThemeColors) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: cfg.backgroundDefault,
      },
    },
  },
  // The Page container paints its own background from the v4 theme, which
  // does not inherit the palette override above.
  BackstagePage: {
    styleOverrides: {
      root: {
        backgroundColor: cfg.backgroundDefault,
      },
    },
  },
  BackstageHeader: {
    styleOverrides: {
      header: {
        backgroundColor: cfg.backgroundPaper,
        backgroundImage: 'none',
        boxShadow: 'none',
        paddingTop: 16,
        paddingBottom: 16,
        borderStyle: 'solid',
        borderWidth: '0 0 3px 0',
        borderImage: `${TE_SPECTRUM} 1`,
      },
      title: {
        color: cfg.ink,
        fontFamily: FONT_DISPLAY,
        fontSize: 26,
        fontWeight: 750,
        letterSpacing: '-0.01em',
      },
      subtitle: {
        color: cfg.inkSecondary,
      },
      type: {
        color: cfg.inkSecondary,
        fontFamily: FONT_DATA,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.11em',
        textTransform: 'uppercase' as const,
      },
    },
  },
  BackstageHeaderLabel: {
    styleOverrides: {
      label: {
        color: cfg.inkSecondary,
        fontFamily: FONT_DATA,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.11em',
        textTransform: 'uppercase' as const,
      },
      value: {
        color: cfg.ink,
        fontSize: 13,
      },
    },
  },
  BackstageHeaderTabs: {
    styleOverrides: {
      defaultTab: {
        fontSize: 12.5,
        fontWeight: 600,
        letterSpacing: '0.05em',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: cfg.primary,
        height: 2,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        fontWeight: 600,
        borderRadius: 8,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: 10,
      },
    },
  },
  // Ink links with a subtle brand underline, not full blue.
  MuiLink: {
    styleOverrides: {
      root: {
        color: cfg.ink,
        textDecoration: 'underline',
        textDecorationColor: `${cfg.primary}66`,
        textUnderlineOffset: '2px',
      },
    },
  },
  // Brand gradient banner on item cards (page headers stay flat).
  BackstageItemCardHeader: {
    styleOverrides: {
      root: {
        backgroundImage: `linear-gradient(120deg, ${cfg.cardColors[0]}, ${cfg.cardColors[1]}) !important`,
        color: '#ffffff !important',
        '& a, & .MuiLink-root, & .MuiLink-root svg': {
          color: '#ffffff !important',
        },
      },
    },
  },
});

// Single theme factory shared by both variants.
const buildTheme = (variant: Variant): UnifiedTheme => {
  const cfg = themeConfig[variant];
  const flat = flatPageTheme(cfg.backgroundPaper, cfg.ink);

  return createUnifiedTheme({
    ...createBaseThemeOptions({
      palette: {
        ...palettes[variant],
        primary: { main: cfg.primary },
        secondary: { main: cfg.secondary },
        error: { main: cfg.error },
        warning: { main: cfg.warning },
        info: { main: cfg.info },
        success: { main: cfg.success },
        divider: cfg.divider,
        text: {
          primary: cfg.ink,
          secondary: cfg.inkSecondary,
        },
        background: {
          default: cfg.backgroundDefault,
          paper: cfg.backgroundPaper,
        },
        navigation: {
          background: cfg.navigationBackground,
          indicator: cfg.navigationIndicator,
          color: cfg.navigationColor,
          selectedColor: cfg.navigationSelectedColor,
        },
      },
    }),
    fontFamily: FONT_UI,
    typography,
    defaultPageTheme: 'home',
    pageTheme: {
      home: flat,
      documentation: flat,
      tool: flat,
      service: flat,
      website: flat,
      library: flat,
      other: flat,
      app: flat,
      apis: flat,
      // Banner behind item cards (e.g. scaffolder template tiles) keeps a
      // gradient so the tiles stay distinguishable.
      card: genPageTheme({ colors: cfg.cardColors, shape: shapes.wave }),
    },
    components: buildComponents(cfg),
  });
};

export const customLightTheme = buildTheme('light');
export const customDarkTheme = buildTheme('dark');
